/**
 * The rule that `safeSpecs` exists only for a winder that IS a safe.
 *
 * Its own module rather than a private helper in firebase.ts, following
 * `winderTurnsPerDay.ts` — which encodes the sibling rule that only one of the
 * two turns-per-day shapes is ever written. Both are invariants the TYPE cannot
 * express (a type cannot say "this object is null unless that boolean is true"),
 * so they live on the write path, where every caller passes through, rather than
 * in the form, which a bulk enrichment apply bypasses entirely.
 */
import type { WinderEntry } from '../types';

export type WinderSafeSpecs = NonNullable<WinderEntry['safeSpecs']>;

/**
 * The ten fields the block can carry, in the order the editor shows them.
 *
 * The three fire fields sit together and read as one sentence — "60 minutes at
 * 927°C, interior below 177°C". They are one class stated three ways, and
 * splitting them up is what made the interior figure look like a poor rating on
 * its own.
 */
export const SAFE_SPEC_KEYS = [
  'certification',
  'fireRatingMinutes',
  'fireExternalTempC',
  'fireMaxInternalTempC',
  'weightKg',
  'lockingMechanism',
  'lockingBoltCount',
  'wallThicknessMm',
  'hasAnchorPoints',
  'insuranceCashRating',
] as const;

/** The locking mechanisms the type allows, as a runtime list for the editor. */
export const LOCKING_MECHANISMS = [
  'key',
  'combination',
  'electronic_keypad',
  'biometric',
  'dual',
] as const;

/** Human labels for the select. */
export const LOCKING_MECHANISM_LABELS: Record<string, string> = {
  key: 'Key',
  combination: 'Combination',
  electronic_keypad: 'Electronic keypad',
  biometric: 'Biometric',
  dual: 'Dual',
};

/**
 * What `saveWinder` writes to `safeSpecs`.
 *
 * Returns null — clearing the block — in two cases, and they are different
 * facts that happen to share an outcome:
 *
 *  - the winder is not a safe, so the block cannot apply. Unticking "is a safe"
 *    and saving MUST clear it; leaving orphaned security specs on an ordinary
 *    winder would have the RN app hide them (it gates on `isSafe`) while the
 *    document still carried them, which is the worst of both.
 *  - every field is null, so the block says nothing. An empty map on the
 *    document is indistinguishable to a reader from "we checked and found
 *    nothing", and it makes `isSafe && safeSpecs` a useless test.
 *
 * Otherwise returns the block with only its populated fields, so a null field
 * is absent rather than explicitly null — matching how every other optional
 * winder field is stored.
 */
export function resolveSafeSpecsWrite(
  isSafe: boolean,
  input?: WinderEntry['safeSpecs'],
): WinderSafeSpecs | null {
  if (!isSafe || !input) return null;

  const out: Record<string, unknown> = {};
  for (const key of SAFE_SPEC_KEYS) {
    const value = (input as Record<string, unknown>)[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out[key] = trimmed;
      continue;
    }
    if (typeof value === 'number') {
      // A non-finite number is not a measurement, and Firestore rejects NaN.
      if (Number.isFinite(value)) out[key] = value;
      continue;
    }
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }

  return Object.keys(out).length > 0 ? (out as WinderSafeSpecs) : null;
}
